/**
 * Shared queue wiring + scope filtering for the two Practice Arena
 * runners (FlashcardRunner + QuizSessionShell).
 *
 * Both runners need the same six lines of `usePracticeQueue` plumbing
 * plus a `conceptIds` allow-list filter; the differences are isolated
 * to:
 *   - `questionsPerConcept` (1 for flashcard, 2 for quiz)
 *   - flashcard's `weak`-scope post-filter (low last_grade / high
 *     struggle_score) - quiz doesn't apply it because the scope is
 *     usually 'custom' or 'starred' by the time the quiz runner mounts
 *   - quiz drops concepts whose `questions` list is empty (a blank quiz
 *     concept would render an empty card)
 *
 * The hook absorbs all three. Callers pass `mode` and get a stable
 * `concepts` array back. Replacing the inline duplication with this
 * hook turns 30 lines × 2 sites into one source of truth.
 */

import { useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MindspaceConcept, QueuePayload } from '@/lib/api-client';
import { usePracticeQueue, revisionKeys } from '@/hooks/mindspace/useRevision';
import type { PracticeMode, SessionConfig } from '@/lib/practice/session-types';
import { buildQueueArgs, filterSessionConcepts } from '@/lib/practice/deck';

interface UsePracticeSessionResult {
  /** Raw queue payload - null while loading, never undefined. */
  queue: QueuePayload | null;
  /** Concepts pre-filter (post-fetch). Quiz runner exposes this so it
   *  can distinguish "no concepts at all" from "concepts exist but
   *  all had empty question lists". */
  rawConcepts: MindspaceConcept[];
  /** Concepts after mode-specific + conceptIds filtering. This is what
   *  the runner actually iterates over. */
  concepts: MindspaceConcept[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** Manually re-run the queue fetch. Used by quiz's Retry button. */
  refetch: () => void;
  /** Drop the cached queue for this session so a next mount fetches
   *  fresh. Useful when the runner ends a session and the orchestrator
   *  wants to invalidate downstream caches. */
  invalidate: () => void;
}

export function usePracticeSession(
  config: SessionConfig,
  mode: PracticeMode
): UsePracticeSessionResult {
  const qc = useQueryClient();

  // The query args — limit, questions-per-concept, the starred/collection
  // switches — are a rule, and they live in `lib/practice/deck` with a table.
  // `questionsPerConcept` in particular is part of the CACHE KEY, so a bug
  // there silently serves one mode's deck to the other.
  const args = buildQueueArgs(config, mode);
  const limit = args.limit;

  const {
    data: queue,
    isLoading,
    isFetching,
    error,
    refetch,
  } = usePracticeQueue(args);

  const rawConcepts = queue?.concepts ?? [];

  const concepts = useMemo(
    () =>
      filterSessionConcepts(rawConcepts, {
        mode,
        scope: config.scope,
        conceptIds: config.conceptIds,
        limit,
      }),
    [rawConcepts, mode, config.scope, config.conceptIds, limit]
  );

  // Freeze the deck for the lifetime of the session. Rating a card fires a
  // review whose `onSuccess` invalidates the whole `['mindspace']` query
  // tree - including THIS queue - so it refetches and the backend returns
  // the now-smaller due set. Without freezing, the live `concepts` array
  // shrinks under the runner mid-session: the count drifts and, once the
  // current index passes the shrunken end, the card goes blank. The runner
  // unmounts when the session ends (phase → results), so this ref resets
  // naturally for the next session.
  const frozenRef = useRef<MindspaceConcept[] | null>(null);
  if (frozenRef.current === null && !isLoading && concepts.length > 0) {
    frozenRef.current = concepts;
  }
  const stableConcepts = frozenRef.current ?? concepts;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: revisionKeys.all });
  };

  return {
    queue: queue ?? null,
    rawConcepts,
    concepts: stableConcepts,
    isLoading,
    isFetching,
    error,
    refetch: () => {
      void refetch();
    },
    invalidate,
  };
}
